import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';
import { UserReturn } from '../clients/rust-api-client';
import { userService } from '../clients/ApiClients';
import {
  generateInitials,
  getCompanyIdFromToken,
  getToken,
  getUserIdFromToken,
  getUserTypeFromToken,
  subscribeToToken,
} from '../utils/TokenUtils';

/**
 * The signed-in manager, loaded from `GET /user/{id}`.
 *
 * `fullName` and `email` have to be a fetch rather than a token decode.
 * The management `Claims` carries `sub`, `user_type`, `company_id`,
 * `hoa_id` and nothing human — no name, no email. Reading them off the
 * JWT is what made the sidebar avatar blank and signed every outgoing
 * message "HOA Administrator".
 *
 * `userId`, `userType` and `companyId` stay token-derived: those claims
 * genuinely exist, and they are needed before this fetch resolves so
 * role-gated nav does not flicker.
 */
interface UserContextType {
  userId: string | null;
  userType: string | null;
  companyId: string | null;
  /** Null until the profile loads, or if it failed. */
  email: string | null;
  fullName: string | null;
  /** Initials for the avatar. Empty until the name is known. */
  initials: string;
  loading: boolean;
  /** Set when the profile could not be loaded. */
  error: string | null;
  reload: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<UserReturn | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState<number>(0);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  // Subscribe rather than read during render.
  //
  // This provider sits above `<Routes>`, and `<App />` is a stable element
  // created once in `index.tsx`, so React skips re-rendering it when the
  // router navigates. Reading `getToken()` during render therefore did not
  // pick up a sign-in — the profile fetched at mount was kept for the whole
  // session, and after signing out and back in as somebody else the sidebar
  // still showed the previous user's email.
  //
  // Storage is not reactive on its own, so `TokenUtils` announces changes
  // and this subscribes to them.
  const token = useSyncExternalStore(subscribeToToken, getToken);

  // The hourly token refresh mints a new token for the same person. Keying
  // the fetch on the user rather than the token itself keeps that from
  // re-fetching a profile we already have.
  const userId = token ? getUserIdFromToken() : null;

  useEffect(() => {
    // No usable token means the router is about to send us to /login;
    // there is nothing to fetch and a 401 here would only race that
    // redirect. `userId` is null whenever the token is missing or
    // unreadable, so it covers both.
    if (!userId) {
      setUser(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await userService.getUser({ id: userId });
        if (cancelled) return;
        setUser(data);
      } catch (err) {
        if (cancelled) return;
        // A 401 is already handled by the response interceptor, which
        // redirects to /login. Anything else is worth surfacing.
        console.error('Failed to load user profile:', err);
        setError("We couldn't load your account.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [userId, reloadCount]);

  const value = useMemo<UserContextType>(
    () => ({
      userId,
      userType: getUserTypeFromToken(),
      companyId: getCompanyIdFromToken(),
      email: user?.email ?? null,
      fullName: user?.full_name ?? null,
      initials: user?.full_name ? generateInitials(user.full_name) : '',
      loading,
      error,
      reload,
    }),
    // `token` is a dependency because `userType` and `companyId` are read
    // fresh from it on each recompute; without it they would be stale from
    // whatever the token was when this provider first rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, error, reload, token, userId]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * The signed-in manager's name for use as a message sender.
 *
 * Falls back to a generic label while the profile is still loading or if
 * it failed — the alternative is signing a resident-facing message with
 * an empty string.
 */
export const useSenderName = (): string => {
  const { fullName } = useUser();
  return fullName || 'HOA Administrator';
};
